/**
 * AdminErrorsPage - Inbox Errori Sistema per Admin
 * Visualizza, filtra e gestisce gli errori di sistema
 */
import React, { useEffect, useState, useCallback } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { it } from 'date-fns/locale';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { ScrollArea } from '../components/ui/scroll-area';
import { Skeleton } from '../components/ui/skeleton';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertTriangle,
  AlertCircle,
  AlertOctagon,
  CheckCircle,
  Search,
  RefreshCw,
  Trash2,
  Eye,
  Filter,
  Loader2,
  XCircle,
  Clock,
  Server,
  Database,
  CreditCard,
  Shield,
  Zap,
  FileWarning,
  ChevronLeft,
  ChevronRight,
  Check
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

interface ErrorLog {
  id: string;
  type: string;
  message: string;
  endpoint?: string;
  user_id?: string;
  severity: string;
  context?: string;
  extra_data?: Record<string, any>;
  stack_trace?: string;
  exception_type?: string;
  resolved: boolean;
  resolved_at?: string;
  resolved_by?: string;
  notes?: string;
  created_at: string;
}

interface ErrorStats {
  critical: number;
  error: number;
  warning: number;
  total_unresolved: number;
  today: number;
  by_type: Array<{ type: string; count: number }>;
}

const SEVERITY_CONFIG: Record<string, { label: string; color: string; icon: React.ReactNode; bgColor: string }> = {
  critical: { 
    label: 'Critico', 
    color: 'text-red-600', 
    icon: <AlertOctagon className="h-4 w-4" />,
    bgColor: 'bg-red-100 dark:bg-red-900/30'
  },
  error: { 
    label: 'Errore', 
    color: 'text-orange-600', 
    icon: <AlertCircle className="h-4 w-4" />,
    bgColor: 'bg-orange-100 dark:bg-orange-900/30'
  },
  warning: { 
    label: 'Warning', 
    color: 'text-yellow-600', 
    icon: <AlertTriangle className="h-4 w-4" />,
    bgColor: 'bg-yellow-100 dark:bg-yellow-900/30'
  },
};

const TYPE_CONFIG: Record<string, { label: string; icon: React.ReactNode }> = {
  api_error: { label: 'Errore API', icon: <Zap className="h-4 w-4" /> },
  auth_error: { label: 'Autenticazione', icon: <Shield className="h-4 w-4" /> },
  validation_error: { label: 'Validazione', icon: <FileWarning className="h-4 w-4" /> },
  database_error: { label: 'Database', icon: <Database className="h-4 w-4" /> },
  integration_error: { label: 'Integrazione', icon: <Server className="h-4 w-4" /> },
  system_error: { label: 'Sistema', icon: <AlertCircle className="h-4 w-4" /> },
  payment_error: { label: 'Pagamento', icon: <CreditCard className="h-4 w-4" /> },
};

const AdminErrorsPage: React.FC = () => {
  const [errors, setErrors] = useState<ErrorLog[]>([]);
  const [stats, setStats] = useState<ErrorStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Filtri
  const [searchQuery, setSearchQuery] = useState('');
  const [severityFilter, setSeverityFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [resolvedFilter, setResolvedFilter] = useState<string>('unresolved');
  
  // Paginazione
  const [page, setPage] = useState(0);
  const [totalErrors, setTotalErrors] = useState(0);
  const limit = 20;
  
  // Selezione
  const [selectedErrors, setSelectedErrors] = useState<string[]>([]);
  
  // Dialog
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedError, setSelectedError] = useState<ErrorLog | null>(null);
  const [resolveNotes, setResolveNotes] = useState('');
  const [resolving, setResolving] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/errors/stats`);
      setStats(res.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  const fetchErrors = useCallback(async (showLoader = true) => {
    if (showLoader) setLoading(true);
    else setRefreshing(true);
    
    try {
      const params: any = {
        limit,
        offset: page * limit,
      };
      
      if (searchQuery) params.search = searchQuery;
      if (severityFilter) params.severity = severityFilter;
      if (typeFilter) params.error_type = typeFilter;
      if (resolvedFilter === 'resolved') params.resolved = true;
      else if (resolvedFilter === 'unresolved') params.resolved = false;
      
      const res = await axios.get(`${API}/admin/errors/list`, { params });
      setErrors(res.data.errors || []);
      setTotalErrors(res.data.total || 0);
    } catch (error) {
      console.error('Error fetching errors:', error);
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [page, searchQuery, severityFilter, typeFilter, resolvedFilter]);

  useEffect(() => {
    fetchStats();
    fetchErrors();
  }, [fetchStats, fetchErrors]);

  const handleRefresh = () => {
    fetchStats();
    fetchErrors(false);
  };

  const handleResolve = async (errorId: string, notes?: string) => {
    setResolving(true);
    try {
      await axios.post(`${API}/admin/errors/${errorId}/resolve`, { notes });
      toast.success('Errore segnato come risolto');
      setDetailDialogOpen(false);
      setSelectedError(null);
      setResolveNotes('');
      fetchStats();
      fetchErrors(false);
    } catch (error) {
      toast.error('Errore nella risoluzione');
    } finally {
      setResolving(false);
    }
  };

  const handleResolveMultiple = async () => {
    if (selectedErrors.length === 0) return;
    
    try {
      await axios.post(`${API}/admin/errors/resolve-multiple`, {
        error_ids: selectedErrors
      });
      toast.success(`${selectedErrors.length} errori risolti`);
      setSelectedErrors([]);
      fetchStats();
      fetchErrors(false);
    } catch (error) {
      toast.error('Errore nella risoluzione multipla');
    }
  };

  const toggleErrorSelection = (errorId: string) => {
    setSelectedErrors(prev => 
      prev.includes(errorId) 
        ? prev.filter(id => id !== errorId)
        : [...prev, errorId]
    );
  };

  const selectAllErrors = () => {
    if (selectedErrors.length === errors.filter(e => !e.resolved).length) {
      setSelectedErrors([]);
    } else {
      setSelectedErrors(errors.filter(e => !e.resolved).map(e => e.id));
    }
  };

  const openErrorDetail = (error: ErrorLog) => {
    setSelectedError(error);
    setResolveNotes('');
    setDetailDialogOpen(true);
  };

  const formatDate = (dateStr: string) => {
    try {
      return formatDistanceToNow(new Date(dateStr), { addSuffix: true, locale: it });
    } catch {
      return dateStr;
    }
  };

  const totalPages = Math.ceil(totalErrors / limit);

  if (loading) {
    return (
      <div className="space-y-6 p-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-errors-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold">Errori Sistema</h1>
          <p className="text-muted-foreground mt-1">
            Monitora e gestisci gli errori della piattaforma
          </p>
        </div>
        <Button variant="outline" onClick={handleRefresh} disabled={refreshing}>
          <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Aggiorna
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className={stats?.critical ? 'border-red-300 bg-red-50/50 dark:bg-red-900/10' : ''}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Critici</CardTitle>
            <AlertOctagon className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{stats?.critical || 0}</div>
            <p className="text-xs text-muted-foreground">non risolti</p>
          </CardContent>
        </Card>
        
        <Card className={stats?.error ? 'border-orange-300 bg-orange-50/50 dark:bg-orange-900/10' : ''}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Errori</CardTitle>
            <AlertCircle className="h-4 w-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">{stats?.error || 0}</div>
            <p className="text-xs text-muted-foreground">non risolti</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Warning</CardTitle>
            <AlertTriangle className="h-4 w-4 text-yellow-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">{stats?.warning || 0}</div>
            <p className="text-xs text-muted-foreground">non risolti</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Oggi</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.today || 0}</div>
            <p className="text-xs text-muted-foreground">nuovi errori</p>
          </CardContent>
        </Card>
      </div>

      {/* Filtri e Azioni */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Cerca errori..."
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
            className="pl-9"
          />
        </div>
        
        <Select value={severityFilter} onValueChange={(v) => { setSeverityFilter(v === 'all' ? '' : v); setPage(0); }}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Severità" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte</SelectItem>
            <SelectItem value="critical">Critici</SelectItem>
            <SelectItem value="error">Errori</SelectItem>
            <SelectItem value="warning">Warning</SelectItem>
          </SelectContent>
        </Select>
        
        <Select value={typeFilter} onValueChange={(v) => { setTypeFilter(v === 'all' ? '' : v); setPage(0); }}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Tipo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti</SelectItem>
            {Object.entries(TYPE_CONFIG).map(([key, { label }]) => (
              <SelectItem key={key} value={key}>{label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <Select value={resolvedFilter} onValueChange={(v) => { setResolvedFilter(v); setPage(0); }}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Stato" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti</SelectItem>
            <SelectItem value="unresolved">Non risolti</SelectItem>
            <SelectItem value="resolved">Risolti</SelectItem>
          </SelectContent>
        </Select>
        
        {selectedErrors.length > 0 && (
          <Button variant="outline" onClick={handleResolveMultiple}>
            <Check className="h-4 w-4 mr-2" />
            Risolvi ({selectedErrors.length})
          </Button>
        )}
      </div>

      {/* Tabella Errori */}
      <Card>
        <CardContent className="p-0">
          {errors.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <CheckCircle className="h-12 w-12 mb-4 text-green-500" />
              <p className="font-medium">Nessun errore trovato</p>
              <p className="text-sm">Il sistema sta funzionando correttamente</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox 
                      checked={selectedErrors.length === errors.filter(e => !e.resolved).length && errors.some(e => !e.resolved)}
                      onCheckedChange={selectAllErrors}
                    />
                  </TableHead>
                  <TableHead>Severità</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead className="max-w-md">Messaggio</TableHead>
                  <TableHead>Endpoint</TableHead>
                  <TableHead>Data</TableHead>
                  <TableHead>Stato</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {errors.map(error => {
                  const severityConfig = SEVERITY_CONFIG[error.severity] || SEVERITY_CONFIG.error;
                  const typeConfig = TYPE_CONFIG[error.type] || { label: error.type, icon: <AlertCircle className="h-4 w-4" /> };
                  
                  return (
                    <TableRow 
                      key={error.id} 
                      className={error.resolved ? 'opacity-60' : ''}
                    >
                      <TableCell>
                        {!error.resolved && (
                          <Checkbox
                            checked={selectedErrors.includes(error.id)}
                            onCheckedChange={() => toggleErrorSelection(error.id)}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${severityConfig.bgColor} ${severityConfig.color}`}>
                          {severityConfig.icon}
                          {severityConfig.label}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5 text-sm">
                          {typeConfig.icon}
                          {typeConfig.label}
                        </div>
                      </TableCell>
                      <TableCell className="max-w-md">
                        <p className="truncate text-sm">{error.message}</p>
                      </TableCell>
                      <TableCell>
                        <code className="text-xs bg-muted px-1.5 py-0.5 rounded">
                          {error.endpoint || '-'}
                        </code>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDate(error.created_at)}
                      </TableCell>
                      <TableCell>
                        {error.resolved ? (
                          <Badge variant="secondary" className="bg-green-100 text-green-700">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Risolto
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-orange-600 border-orange-300">
                            <Clock className="h-3 w-3 mr-1" />
                            Aperto
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => openErrorDetail(error)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
        
        {/* Paginazione */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t">
            <p className="text-sm text-muted-foreground">
              Mostrando {page * limit + 1}-{Math.min((page + 1) * limit, totalErrors)} di {totalErrors}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 0}
                onClick={() => setPage(p => p - 1)}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm">
                Pagina {page + 1} di {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages - 1}
                onClick={() => setPage(p => p + 1)}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Dialog Dettaglio Errore */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedError && SEVERITY_CONFIG[selectedError.severity]?.icon}
              Dettaglio Errore
              <code className="text-xs bg-muted px-2 py-0.5 rounded ml-2">
                {selectedError?.id}
              </code>
            </DialogTitle>
            <DialogDescription>
              {selectedError && formatDate(selectedError.created_at)}
            </DialogDescription>
          </DialogHeader>
          
          {selectedError && (
            <div className="space-y-4">
              {/* Info base */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-muted-foreground">Severità</label>
                  <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${SEVERITY_CONFIG[selectedError.severity]?.bgColor} ${SEVERITY_CONFIG[selectedError.severity]?.color}`}>
                    {SEVERITY_CONFIG[selectedError.severity]?.icon}
                    {SEVERITY_CONFIG[selectedError.severity]?.label}
                  </div>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Tipo</label>
                  <p className="text-sm font-medium">{TYPE_CONFIG[selectedError.type]?.label || selectedError.type}</p>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Endpoint</label>
                  <code className="text-sm bg-muted px-2 py-0.5 rounded block mt-1">
                    {selectedError.endpoint || '-'}
                  </code>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">User ID</label>
                  <p className="text-sm">{selectedError.user_id || '-'}</p>
                </div>
              </div>
              
              {/* Messaggio */}
              <div>
                <label className="text-xs text-muted-foreground">Messaggio</label>
                <p className="text-sm bg-muted p-3 rounded-lg mt-1">{selectedError.message}</p>
              </div>
              
              {/* Stack trace */}
              {selectedError.stack_trace && (
                <div>
                  <label className="text-xs text-muted-foreground">Stack Trace</label>
                  <ScrollArea className="h-40 mt-1">
                    <pre className="text-xs bg-muted p-3 rounded-lg overflow-x-auto">
                      {selectedError.stack_trace}
                    </pre>
                  </ScrollArea>
                </div>
              )}
              
              {/* Extra data */}
              {selectedError.extra_data && Object.keys(selectedError.extra_data).length > 0 && (
                <div>
                  <label className="text-xs text-muted-foreground">Dati Aggiuntivi</label>
                  <pre className="text-xs bg-muted p-3 rounded-lg mt-1 overflow-x-auto">
                    {JSON.stringify(selectedError.extra_data, null, 2)}
                  </pre>
                </div>
              )}
              
              {/* Note risoluzione (se risolto) */}
              {selectedError.resolved && selectedError.notes && (
                <div>
                  <label className="text-xs text-muted-foreground">Note Risoluzione</label>
                  <p className="text-sm bg-green-50 dark:bg-green-900/20 p-3 rounded-lg mt-1 border border-green-200">
                    {selectedError.notes}
                  </p>
                </div>
              )}
              
              {/* Form risoluzione (se non risolto) */}
              {!selectedError.resolved && (
                <div>
                  <label className="text-xs text-muted-foreground">Note (opzionale)</label>
                  <Textarea
                    value={resolveNotes}
                    onChange={(e) => setResolveNotes(e.target.value)}
                    placeholder="Aggiungi una nota sulla risoluzione..."
                    rows={2}
                    className="mt-1"
                  />
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailDialogOpen(false)}>
              Chiudi
            </Button>
            {selectedError && !selectedError.resolved && (
              <Button 
                onClick={() => handleResolve(selectedError.id, resolveNotes)}
                disabled={resolving}
              >
                {resolving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                <CheckCircle className="h-4 w-4 mr-2" />
                Segna Risolto
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminErrorsPage;
